import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/RefreshOutlined'
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined'
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined'
import CancelIcon from '@mui/icons-material/CancelOutlined'
import BeachAccessIcon from '@mui/icons-material/BeachAccessOutlined'
import DoneAllIcon from '@mui/icons-material/DoneAllOutlined'
import GroupIcon from '@mui/icons-material/GroupOutlined'
import type { SvgIconComponent } from '@mui/icons-material'
import { flattenEntries, useDashboard } from '../api/dashboard'
import type { DashboardGroup, DashboardRow } from '../api/dashboard'
import { useBranches } from '../api/branches'
import { errorMessage } from '../api/client'
import { StatusChip } from '../components/StatusChip'
import { formatDate, formatDuration, formatRelative, formatTime } from '../lib/format'

type Filter = 'all' | DashboardGroup

interface SummaryCard {
  key: Filter
  label: string
  value: number
  icon: SvgIconComponent
  color: string
}

export function DashboardPage() {
  const today = new Date().toISOString().slice(0, 10)

  const [branchId, setBranchId] = useState<number | ''>('')
  const [date, setDate] = useState(today)
  const [filter, setFilter] = useState<Filter>('all')

  const { data: branches } = useBranches()
  const { data, isLoading, isFetching, error, refetch, dataUpdatedAt } = useDashboard({
    branchId: branchId === '' ? undefined : branchId,
    date: date === today ? undefined : date,
  })

  const entries = useMemo(() => (data ? flattenEntries(data) : []), [data])

  const visible = useMemo(
    () => (filter === 'all' ? entries : entries.filter((entry) => entry.group === filter)),
    [entries, filter],
  )

  const cards: SummaryCard[] = data
    ? [
        { key: 'all', label: 'Empleados', value: data.summary.total_employees, icon: GroupIcon, color: 'text.primary' },
        { key: 'present', label: 'Presentes', value: data.summary.present, icon: CheckCircleIcon, color: 'success.main' },
        { key: 'late', label: 'Tarde', value: data.summary.late, icon: ScheduleIcon, color: 'warning.main' },
        { key: 'completed', label: 'Jornada cerrada', value: data.summary.completed, icon: DoneAllIcon, color: 'info.main' },
        { key: 'absent', label: 'Ausentes', value: data.summary.absent, icon: CancelIcon, color: 'error.main' },
        { key: 'on_leave', label: 'Licencia', value: data.summary.on_leave, icon: BeachAccessIcon, color: 'info.main' },
      ]
    : []

  if (error) {
    return <Alert severity="error">{errorMessage(error, 'No se pudo cargar el panel.')}</Alert>
  }

  return (
    <Stack spacing={3}>
      <Card variant="outlined" elevation={0}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ alignItems: { md: 'center' } }}
          >
            <TextField
              select
              label="Sucursal"
              value={branchId}
              onChange={(event) =>
                setBranchId(event.target.value === '' ? '' : Number(event.target.value))
              }
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">Todas las sucursales</MenuItem>
              {branches?.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              type="date"
              label="Fecha"
              value={date}
              onChange={(event) => setDate(event.target.value || today)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 170 }}
            />

            <Box sx={{ flexGrow: 1 }} />

            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                {isFetching
                  ? 'Actualizando…'
                  : dataUpdatedAt
                    ? `Actualizado ${formatRelative(new Date(dataUpdatedAt))}`
                    : ''}
              </Typography>
              <Tooltip title="Actualizar ahora">
                <span>
                  <IconButton onClick={() => refetch()} disabled={isFetching} aria-label="Actualizar">
                    {isFetching ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Jornada del {data ? formatDate(data.work_date) : formatDate(date)}
          {data?.branch ? ` · ${data.branch}` : ' · todas las sucursales'}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              lg: 'repeat(6, 1fr)',
            },
          }}
        >
          {isLoading
            ? Array.from({ length: 6 }, (_, index) => (
                <Skeleton key={index} variant="rounded" height={104} />
              ))
            : cards.map((card) => {
                const selected = filter === card.key

                return (
                  <Card
                    key={card.label}
                    variant="outlined"
                    elevation={0}
                    onClick={() => setFilter(selected ? 'all' : card.key)}
                    sx={{
                      cursor: 'pointer',
                      transition: 'border-color .15s, background-color .15s',
                      borderColor: selected ? card.color : undefined,
                      bgcolor: selected ? 'action.selected' : undefined,
                      '&:hover': { borderColor: card.color },
                    }}
                  >
                    <CardContent>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                        <card.icon sx={{ fontSize: 18, color: card.color }} />
                        <Typography variant="caption" color="text.secondary">
                          {card.label}
                        </Typography>
                      </Stack>
                      <Typography variant="h1" sx={{ color: card.color }}>
                        {card.value}
                      </Typography>
                    </CardContent>
                  </Card>
                )
              })}
        </Box>
      </Box>

      <Card variant="outlined" elevation={0}>
        <CardContent sx={{ pb: 0 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', mb: 1 }}
          >
            <Typography variant="h3">Detalle de la jornada</Typography>

            <ToggleButtonGroup
              size="small"
              exclusive
              value={filter}
              onChange={(_, value: Filter | null) => value && setFilter(value)}
            >
              <ToggleButton value="all">Todos</ToggleButton>
              <ToggleButton value="present">Presentes</ToggleButton>
              <ToggleButton value="late">Tarde</ToggleButton>
              <ToggleButton value="absent">Ausentes</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </CardContent>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Empleado</TableCell>
                <TableCell>Sucursal</TableCell>
                <TableCell align="right">Ingreso</TableCell>
                <TableCell align="right">Egreso</TableCell>
                <TableCell align="right">Trabajado</TableCell>
                <TableCell align="right">Tarde</TableCell>
                <TableCell align="right">Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 3 }}>
                    <Skeleton />
                    <Skeleton />
                    <Skeleton />
                  </TableCell>
                </TableRow>
              ) : visible.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    {entries.length === 0
                      ? 'No hay empleados activos para esta jornada.'
                      : 'Ningún empleado en este estado.'}
                  </TableCell>
                </TableRow>
              ) : (
                visible.map((entry) => <EmployeeRow key={entry.uuid} entry={entry} />)
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Stack>
  )
}

function EmployeeRow({ entry }: { entry: DashboardRow }) {
  return (
    <TableRow hover>
      <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{entry.full_name}</TableCell>
      <TableCell sx={{ color: 'text.secondary' }}>{entry.branch ?? '—'}</TableCell>
      <TableCell align="right">{formatTime(entry.check_in_at)}</TableCell>
      <TableCell align="right">{formatTime(entry.check_out_at)}</TableCell>
      <TableCell align="right">
        {formatDuration(entry.worked_minutes)}
        {entry.overtime_minutes > 0 && (
          <Typography component="span" variant="caption" color="info.main" sx={{ ml: 0.5 }}>
            +{formatDuration(entry.overtime_minutes)}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right" sx={{ color: entry.late_minutes > 0 ? 'warning.main' : 'inherit' }}>
        {entry.late_minutes > 0 ? `${entry.late_minutes} min` : '—'}
      </TableCell>
      <TableCell align="right">
        <StatusChip status={entry.status} />
      </TableCell>
    </TableRow>
  )
}
